import {Dispatch, SetStateAction} from 'react'
import {SearchUsersGroupsItem} from './Settings.types'
import {FileWithPath} from 'react-dropzone'

export enum Policy {
  BasePolicy = 'Base Policy',
}

export enum ExceptionType {
  Group = 'groups',
  User = 'users',
}

export enum SUB_STRING_METHOD {
  StartsWith = 'startsWith',
  Includes = 'includes',
}

export enum POLICY_CATEGORY_TYPE {
  CategoriesV2 = 'categories_v2',
  CustomCategories = 'customCategories',
}

export enum CATEGORY_RESTRICTIONS {
  Allowed = 0,
  Blocked = 1,
  Warning = 2,
  Ignored = 3,
  Mixed = 4,
}

export enum RestrictionTypeEnum {
  Allowed = 'Allowed',
  Blocked = 'Blocked',
  Warning = 'Warning',
  Ignore = 'Ignore',
  Mixed = 'Mixed',
}

export interface SelectedCustomCategoryUrlsMap {
  url: string
  editMode: boolean
}
export interface EditModeItem {
  value: string
  editMode: boolean
  isEditedOrAdded?: boolean
  isNotSaved?: boolean
  traffic?: string
  osType?: string
}
export type CategoriesProps = {
  page: string
  state: number
  description?: string
  exceptions: []
  isOpenForEdit?: boolean
}
export type PoliciesListReactQueryResponse = {
  pages: PoliciesListResponseType[]
}
export type PoliciesListResponseType = {
  data: {
    body: {
      Items: PoliciesItemsTypes[]
      LastEvaluatedKey: PoliciesLastEvaluatedKeyTypes
    }
  }
}
export type PoliciesItemsTypes = {
  policyId: string
  policyName: string
  updatedAt: string
  clashCount?: number
}
export type PoliciesLastEvaluatedKeyTypes = {
  agentUUID_userUUID: string
  tenantId: string
  statusNumber: number
}

export interface CustomCategoryType {
  [x: string]: {
    state: number
    page: BlockPageType | string
    description?: string
    am_id: string
    exceptions: ExceptionsType
    urls?: string[]
    inheritsGlobalCCState?: boolean
    inheritsGlobalCCExceptions?: boolean
    inheritsGlobalCCPage?: boolean
  }
}

export interface CreateCustomCategoryType {
  state: number
  name: string
  error?: string
}

export interface ExceptionsType {
  [x: string]: ExceptionNode
}

export interface ExceptionNode {
  state: number
  page: string
  type?: string
  name?: string
  emailId?: string
}

export interface PolicyContentTypes {
  categories_v2: CategoriesV2Type
  pages: {default_block: string; default_warning: string}
  customCategories: CustomCategoryType
  inheritsCategories: boolean
  inheritsCustomCategories: boolean
}

export interface CategoriesV2Type {
  [x: string]: ParentCategoryV2Type
}

export interface ParentCategoryV2Type extends CategoryV2Type {
  child_categories: ChildCategoryV2Type
}

export interface ChildCategoryV2Type {
  [x: string]: CategoryV2Type
}

export interface CategoryV2Type {
  am_id: string
  description: string
  exceptions: ExceptionsType
  page: BlockPageType | string
  // TODO: Create enum for state numbers i.e allowed, blocked and warning
  // We need to do this in contraction phase because prod policy content is on old APIs
  state: number
}

export interface PolicyBodyTypes extends PolicyContentTypes {
  policyId: string
  policyName: string
  updatedAt: string
  tenantId: string
  createdAt: string
}

export type PolicyDetailsTypes = {
  message: string
  body: PolicyBodyTypes
}

export interface ApplicationBypassType {
  Mac: string[]
  Windows: string[]
}

export interface AssignedPolicyTypes {
  emailId: string
  name: string
  membersCount?: string
  type: string
}

export enum CategoryType {
  default = 'default',
  custom = 'custom',
}

export enum BlockPageType {
  defaultBlock = 'defaultblock',
  defaultWarning = 'defaultwarning',
  defaultSecurityRisk = 'defaultsecurityrisk',
  defaultCloudApplication = 'defaultcloudapplication',
}

export interface CategoryProps {
  categoryName: string
  subCategoryName: string
  isSubCategory: boolean
  description: string
  categoryType: CategoryType
  categoryIndex?: number
}

export interface UpdatePolicyExceptionsPayload {
  data: CategoryExceptionsChanges
  policyName: string
}

export interface UpdatePolicyRestrictionsPayload {
  data: UpdatePolicyRestrictionsData
  policyName: string
}

export type BasePolicyName = 'base'
export interface UpdatePolicyRestrictionsData {
  categories?: CategoryRestrictions | BasePolicyName
  customCategories?: CategoryRestrictions | BasePolicyName
}

export interface UpdateUrlFiltersPayload {
  data: UrlFiltersChanges
}

export interface DeleteCategoriesPayload {
  data: CategoriesChanges
}

export interface AddExceptionParam {
  values: ExceptionNode
  usersGroupsList: SearchUsersGroupsItem[]
  selectedCategory: CategoryProps | null
  selectedPolicyDetails: PolicyBodyTypes
  aSelectedParentCategoryNode: ParentCategoryV2Type
  aSelectedChildCategoryNode: CategoryV2Type
  aSelectedCategoryHasChildCategories: boolean
  setSelectedPolicyDetails: (data: PolicyBodyTypes) => void
  setChanges: (
    data:
      | PolicyChangesState
      | ((prev: PolicyChangesState) => PolicyChangesState),
  ) => void
}

export interface DeleteExceptionParams {
  exceptionName: string
  selectedCategory: CategoryProps
  selectedPolicyDetails: PolicyBodyTypes
  aSelectedParentCategoryNode: ParentCategoryV2Type
  aSelectedChildCategoryNode?: CategoryV2Type
  aSelectedCategoryHasChildCategories: boolean
  setSelectedPolicyDetails: (data: PolicyBodyTypes) => void
  setChanges: (data: (prev: PolicyChangesState) => PolicyChangesState) => void
}

export interface HandlePageChangeParam {
  selectedCategory: CategoryProps | null
  selectedPolicyDetails: PolicyBodyTypes | null
  setSelectedPolicyDetails: (data: PolicyBodyTypes) => void
  restrictions: CategoryRestrictionsChanges
  setChanges: SetChangesType
  pageValue: string
  restrictionStateNum?: number
  aSelectedParentCategoryNode: ParentCategoryV2Type
  aSelectedChildCategoryNode: CategoryV2Type
  aSelectedCategoryHasChildCategories: boolean
}
export interface ChangeDopeCategoryRestrictionParams {
  categoryName: string
  childCategoryName?: string
  selectedPolicyDetails: PolicyBodyTypes
  hasChildCategories: boolean
  parentCategoryNode: ParentCategoryV2Type
  setSelectedPolicyDetails: (data: PolicyBodyTypes) => void
  setChanges: (data: (prev: PolicyChangesState) => PolicyChangesState) => void
}

export interface ChangeCustomCategoryRestrictionParams {
  categoryName: string
  selectedPolicyDetails: PolicyBodyTypes
  setSelectedPolicyDetails: (data: PolicyBodyTypes) => void
  setChanges: (data: (prev: PolicyChangesState) => PolicyChangesState) => void
}

export interface HandleChangeDopeCategoryRestrictionParams {
  categoryName: string
  parentCategoryNode: ParentCategoryV2Type
  hasChildCategories: boolean
  childCategoryName?: string
}

export type PoliciesTesterTypes = {
  policyName: string
  category?: string
  state: 0 | 1 | 2
  isBypassed?: boolean
  otherCategories?: PolicyTesterCategory[]
  activeCategory: PolicyTesterCategory
  clashPolicies?: PolicyTesterClashPolicy[]
}

export interface PolicyTesterCategory {
  categoryName: string
  state: 0 | 1 | 2
}

export interface PolicyTesterClashPolicy {
  policyName: string
  groups: string[]
}

export interface AssignPolicyPayload {
  policyName: string
  delete_users: string[]
  delete_groups: string[]
  add_users: string[]
  add_groups: string[]
}

export interface OpenCategories {
  name: string
  hasChildren: boolean
}

export interface PolicyInheritTagState {
  text: string
  goToBasePolicy: boolean
  showReset: boolean
}

// policy context types
export interface PolicyChangesState {
  exceptions: CategoryExceptionsChanges | null
  restrictions: CategoryRestrictionsChanges | null
  createCategories: CategoriesChanges | null
  urlFilters: UrlFiltersChanges | null
  deleteCategories: CategoriesChanges | null
}

export interface CategoryExceptionsChanges {
  categories?: CategoryExceptions
  customCategories?: CategoryExceptions
}

export interface CategoryExceptions {
  [x: string]: {
    am_id?: string
    exceptions: ExceptionsType
  }
}
export interface CategoryRestrictionsChanges {
  categories?: CategoryRestrictions
  customCategories?: CategoryRestrictions
  resetDopeCategories?: boolean
  resetCustomCategories?: boolean
}

export interface CategoryRestrictionsValues {
  am_id?: string
  state: number
  page: BlockPageType | string
}

export type CategoryRestrictions = Record<string, CategoryRestrictionsValues>

export interface CategoriesChanges {
  categoryNames: string[]
}

export interface CreateCustomCategoriesPayload {
  data: CategoriesChanges
}

export interface CreateCustomCategoryParams {
  selectedPolicyDetails: PolicyBodyTypes
  customCategory: CreateCustomCategoryType
  setSelectedPolicyDetails: (data: PolicyBodyTypes) => void
  setChanges: SetChangesType
}

export interface RenameCustomCategoryParams {
  newCategoryName: string
  currentCategoryName: string
  selectedPolicyDetails: PolicyBodyTypes | null
  setSelectedPolicyDetails: (data: PolicyBodyTypes) => void
  setChanges: SetChangesType
}

export interface UrlFiltersChanges {
  customCategories: UrlFilters
}

export type UrlFilters = Record<
  string,
  {
    // API expects snake_case
    url_filters: string[]
  }
>

// Define the type for setChanges
export type SetChangesType = (
  data: PolicyChangesState | ((prev: PolicyChangesState) => PolicyChangesState),
) => void

export interface DeleteCustomCategoryURLParam {
  categoryName: string
  selectedPolicyDetails: PolicyBodyTypes | null
  setSelectedPolicyDetails: (data: PolicyBodyTypes) => void
  setChanges: SetChangesType
}

export type BlockPageOption = {
  value: string
  label: string
  type?: string
  url?: string
}

export type BGColor = {
  bg: string
  hover: string
  scrollColor: string
}

export interface URLFilteringProps {
  rightPanelContentHeight: number
  bgColor: BGColor
  isSelectedForDelete: boolean
}

export interface URLsList {
  uniqueUrls: string[]
  duplicateUrls: string[]
}

export type CustomOptionType = {
  label: string
  value: string
}

export interface CustomDropzoneProps {
  parsedURLsFromFile: string[]
  rightPanelContentHeight: number
  setParsedURLsFromFile: Dispatch<SetStateAction<string[]>>
  setResetForm: Dispatch<SetStateAction<boolean>>
}

export interface HandleOnDrop {
  acceptedFiles: FileWithPath[]
  handleFileUpload: (fileName: string, binaryStr: string) => void
}
